import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ----------------------------------------------------------------------------
// Benign-error suppression — must register BEFORE React + the CRA dev
// overlay so we get the events in capture phase first.
//
// `ResizeObserver loop completed with undelivered notifications.` is a
// well-known Chromium quirk that fires whenever a ResizeObserver
// callback triggers another layout in the same frame. It's harmless
// (the next frame catches up), but CRA's dev overlay intercepts it as a
// fatal runtime error and obscures the whole app behind a red banner.
//
// Three-layered defence, registered top-down so the cheapest path wins:
//
//   1. Wrap `ResizeObserver` so its callbacks fire via requestAnimationFrame
//      → breaks the synchronous loop at the source, so the browser never
//      emits the "loop completed" warning in the first place. Fixes the
//      Esc-key / mobile-back-button overlay regression (2026-05-13).
//   2. Swallow the warning in capture phase + stopImmediatePropagation +
//      preventDefault → covers any external lib that creates its own
//      `ResizeObserver` before our wrapper runs.
//   3. The `GlobalErrorHandler` component drops it from telemetry too.
//
// Production builds don't ship the dev overlay, so layers 1+2 are
// effectively dev-mode UX fixes. Layer 3 still matters in prod (no
// noisy alerts on /api/_client-error).
// ----------------------------------------------------------------------------
if (typeof window !== "undefined" && typeof window.ResizeObserver === "function") {
  const NativeRO = window.ResizeObserver;
  // Skip if we (or anyone else) already wrapped — idempotent guard so
  // HMR re-runs of index.js don't double-stack frames.
  if (!NativeRO.__deferred) {
    class DeferredResizeObserver extends NativeRO {
      constructor(callback) {
        let raf = 0;
        let pendingEntries = [];
        super((entries, observer) => {
          pendingEntries = entries;
          if (raf) cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            raf = 0;
            try {
              callback(pendingEntries, observer);
            } catch (err) {
              // Genuine errors inside a consumer's callback shouldn't be
              // silently swallowed by our wrapper — re-throw on a fresh
              // microtask so the global error handler still sees them.
              setTimeout(() => {
                throw err;
              }, 0);
            }
          });
        });
      }
    }
    DeferredResizeObserver.__deferred = true;
    window.ResizeObserver = DeferredResizeObserver;
  }
}

if (typeof window !== "undefined") {
  const RESIZE_OBSERVER_PHRASES = [
    "ResizeObserver loop completed with undelivered notifications.",
    "ResizeObserver loop limit exceeded",
  ];
  const isBenign = (msg) => {
    if (typeof msg !== "string") return false;
    return RESIZE_OBSERVER_PHRASES.some((p) => msg.includes(p));
  };
  window.addEventListener(
    "error",
    (e) => {
      if (isBenign(e?.message)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );
  window.addEventListener(
    "unhandledrejection",
    (e) => {
      const m = e?.reason?.message || e?.reason;
      if (isBenign(typeof m === "string" ? m : "")) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register the service worker for PWA install + offline support.
// This is the bootstrap registration — `lib/push.js` later upgrades the
// same registration with a push subscription if the user opts in. We
// only register in production and on https/localhost to avoid CRA dev
// mode confusion (CRA hot-reload conflicts with SW caching). The SW
// itself opts out of caching API responses, so registering it cannot
// break authenticated requests.
if (
  process.env.NODE_ENV === "production"
  && typeof navigator !== "undefined"
  && "serviceWorker" in navigator
) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      // Check for SW updates on every load + every 30 minutes the tab
      // stays open. New SW versions trigger an immediate update so
      // pilots don't get stuck on a stale cache across deploys.
      try {
        reg.update();
        setInterval(() => {
          reg.update().catch(() => {});
        }, 30 * 60 * 1000);
      } catch {
        /* update() not supported on very old browsers */
      }
      // When a new SW takes over, reload the page once so the active
      // tab also picks up the new bundle (guarded by a session flag
      // to avoid reload loops if anything goes sideways).
      let reloaded = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloaded) return;
        reloaded = true;
        try {
          window.location.reload();
        } catch {
          /* noop */
        }
      });
    }).catch(() => {
      /* SW registration is best-effort; failing silently is fine. */
    });
  });
}

// Install-state tracking — the `appinstalled` event fires once on the
// browser tab where the user accepted the install prompt. We persist a
// flag to localStorage so the Landing page can surface an "Open in App"
// CTA on subsequent visits *to that same browser*. Unlike
// `getInstalledRelatedApps()` (Chrome-only, requires a Play link), this
// gives us a cross-browser signal even on Edge / Brave / Samsung etc.
if (typeof window !== "undefined") {
  window.addEventListener("appinstalled", () => {
    try {
      window.localStorage.setItem("pwa-installed", "1");
    } catch {
      /* private mode */
    }
    // Fire-and-forget install-completed beacon. Reads the source
    // persisted by Landing.jsx on `?src=qr` (or other) landings so
    // we can compute QR-driven install conversion later.
    try {
      const source =
        window.localStorage.getItem("install-attribution-source") || "direct";
      const apiBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      // `keepalive` lets the request survive page unload (typical
      // right after a PWA install promotes to standalone mode).
      fetch(`${apiBase}/api/telemetry/install-completed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        keepalive: true,
        body: JSON.stringify({ source }),
      }).catch(() => {});
    } catch {
      /* fail silent */
    }
  });
  // Also flag if the page is currently running in standalone mode —
  // means the PWA is definitely installed and we forgot to mark it.
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator.standalone === true;
  if (isStandalone) {
    try {
      window.localStorage.setItem("pwa-installed", "1");
    } catch {
      /* private mode */
    }
  }
}
